import React, { useMemo, useState } from "react";
import { Box, Button, Stack, Typography } from "@mui/material";
import { resolvedStartHole } from "@moment-golf/shared";
import { useHoleSettings } from "../Context/useHoleSettingsContext";
import { useLSContext } from "../Context/useLSContext";
import { useDataContext } from "../Context/useDataContext";
import { buildHoleEnqueueItems } from "../utils/enqueueHoleScoreUpdates";
import { usePendingScoreContext } from "../Context/PendingScoreContext";

interface ChangeHoleProps {
    changeHoleOpen: boolean;
    setChangeHoleOpen: (changeHoleOpen: boolean) => void;
}

export const ChangeHolePopup = ({ changeHoleOpen, setChangeHoleOpen}: ChangeHoleProps) => {

    const { setCurrentHole, currentHole, setToggleScoring, toggleScoring, isPWA, setScoreLock, windowSize } = useLSContext();
    const { eventData, courseData, scoreData, currentRound, scoreCode, scoreDataRef, startHole } = useDataContext();
    const { holeSettings, setHoleSettings } = useHoleSettings();
    const { enqueue, flushQueue } = usePendingScoreContext();

    const roundTotalHoles = useMemo(() => {
        const fromRound = Number(eventData?.rounds?.[currentRound - 1]?.totalHoles);
        const fromEvent = Number(eventData?.totalHoles);
        return (Number.isFinite(fromRound) && fromRound > 0 ? fromRound : 0)
            || (Number.isFinite(fromEvent) && fromEvent > 0 ? fromEvent : 0)
            || 18;
    }, [eventData?.rounds, eventData?.totalHoles, currentRound]);

    const [newHole, setNewHole] = useState(currentHole);

    const challengeHoles = useMemo<Set<number>>(() => {
        const activeRound = eventData?.rounds?.find((r: any) => r.roundNumber === currentRound);
        const holes = activeRound?.challenges?.map((c: any) => c.hole) ?? [];
        return new Set(holes);
    }, [eventData, currentRound]);

    const playerStartHole = useMemo(() => {
        if (startHole > 0) {
            return startHole;
        }
        const fromScore = scoreData[0]?.roundScores?.[currentRound - 1]?.startingHole;
        if (fromScore != null && Number(fromScore) > 0 && Number.isFinite(Number(fromScore))) {
            return Math.round(Number(fromScore));
        }
        const roundCfg = eventData?.rounds?.find((r: any) => Number(r?.roundNumber) === Number(currentRound))
            ?? eventData?.rounds?.[currentRound - 1];
        return resolvedStartHole(roundCfg);
    }, [startHole, scoreData, currentRound, eventData]);

    const enqueueCurrentHole = () => {
        const items = buildHoleEnqueueItems({
            scoreData,
            scoreDataRef,
            holeNumber: holeSettings.hole,
            currentRound,
            scoreCode,
            eventData,
            roundTotalHoles,
        });

        if (items.length > 0) {
            enqueue(items);
        }
    };

    const handleChangeHole = (value: number) => {
        console.log('[ChangeHolePopup] changing to hole', value, '— enqueuing current hole scores');
        enqueueCurrentHole();
        console.log('[ChangeHolePopup] background flush triggered — navigating immediately');
        void flushQueue();
        newHoleSettings(Number(value));
        setCurrentHole(value);
        setChangeHoleOpen(false);
        setNewHole(1);
    }

    const newHoleSettings = (newHole: number) => {
     
        if (eventData && Object.keys(eventData).length > 0 && 
            courseData && Object.keys(courseData).length > 0 &&
            newHole > 0) {
   
            const playerTeeSet = eventData.playerTeeSet || 
                (courseData.teeSets && courseData.teeSets.length > 0 ? courseData.teeSets[0].teeName : null);
   
            if (playerTeeSet && courseData.teeSets) {
                const teeSet = courseData.teeSets.find((tee: any) => tee.teeName === playerTeeSet);
   
                if (teeSet && teeSet.holes && teeSet.holes.length >= newHole) {
                    const teesData = teeSet.holes[newHole-1];
   
                    setHoleSettings( {
                            hole: newHole,
                            par: teesData.par,
                            dist: teesData.dist,
                            hdcp: teesData.hdcp
                    });
                }
            }
        }
    }

    return (
        changeHoleOpen && <>
            {/* <PopupMessageWrapper open={changeHoleOpen}> */}
                <Stack direction="column" spacing={4} alignItems="center" sx={{ width: '100%' }}>
                    <Stack direction="column" spacing={1} alignItems="center"> 
                        <Typography variant="h6" sx={{ color: 'sepia.dark', fontWeight: 700 }}>Change Hole</Typography>
                        <Typography variant="body1" sx={{ color: 'sepia.main' }}>Select the hole you would like to play</Typography>
                    </Stack>
                    <Box
                        sx={{
                            width: '100%',
                            display: 'grid',
                            gridTemplateColumns: 'repeat(4, 1fr)',
                            gap: '6px',
                        }}
                    >
                        {Array.from({ length: scoreData[0].roundScores[currentRound-1].holeScores.length }, (_, index) => {
                            const holeNumber = index + 1;
                            const hasScore = scoreData[0].roundScores[currentRound-1].holeScores[index].playerScore !== 0 &&
                                scoreData[0].roundScores[currentRound-1].holeScores[index].playerScore !== null;
                            const hasChallenge = challengeHoles.has(holeNumber);
                            const isStartHole = holeNumber === playerStartHole;
                            const startRingColor = hasScore ? 'primary.main' : 'secondary.main';
                            const startLabelColor = hasScore ? 'primary.dark' : 'secondary.main';
                            return (
                                <Button
                                    key={index}
                                    variant="contained"
                                    color={hasScore ? 'secondary' : 'primary'}
                                    sx={{
                                        width: '100%',
                                        minWidth: 0,
                                        height: 62,
                                        p: 0.5,
                                        fontSize: '1.15rem',
                                        fontWeight: 600,
                                        lineHeight: 1,
                                        display: 'flex',
                                        flexDirection: 'column',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        gap: 0,
                                        ...(hasChallenge && {
                                            boxShadow: '0 0 8px 3px rgba(184, 115, 51, 0.65)',
                                            outline: '1.5px solid rgba(184, 115, 51, 0.8)',
                                        }),
                                    }}
                                    onClick={() => handleChangeHole(holeNumber)}
                                >
                                    {isStartHole ? (
                                        <>
                                            <Box
                                                sx={{
                                                    width: 38,
                                                    height: 38,
                                                    borderRadius: '50%',
                                                    border: '2px solid',
                                                    borderColor: startRingColor,
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    justifyContent: 'center',
                                                    flexShrink: 0,
                                                    fontSize: '1.15rem',
                                                    fontWeight: 600,
                                                    lineHeight: 1,
                                                }}
                                            >
                                                {holeNumber}
                                            </Box>
                                            <Typography
                                                component="span"
                                                sx={{
                                                    fontWeight: 700,
                                                    fontSize: '0.6rem',
                                                    lineHeight: 1,
                                                    mt: 0.25,
                                                    color: startLabelColor,
                                                    letterSpacing: '0.02em',
                                                }}
                                            >
                                                Start
                                            </Typography>
                                        </>
                                    ) : (
                                        holeNumber
                                    )}
                                </Button>
                            );
                        })}
                    </Box>
                </Stack>
           {/*  </PopupMessageWrapper> */}
          </>
    )
}